import type { SchematicExportFrame } from './schematic-export'

/**
 * La **rastérisation du schéma en JPEG**, faite par la tablette (M11 : le JSON reste éditable,
 * l'image garantit que l'archive se relit dans dix ans). Aucun rendu côté serveur.
 *
 * Un port, pas un appel direct au DOM — même intention que `ports/geolocation.ts`. jsdom n'a ni
 * décodeur d'image ni `canvas.toBlob`, un navigateur peut refuser un canvas trop grand, et la
 * conversion échoue pour de vrai sur une tablette à court de mémoire. Rendre la capacité
 * injectable est ce qui permet d'éprouver ces cas plutôt que de les supposer.
 */
export interface SchematicImagePort {
  /**
   * ⚠️ **Ne rejette jamais.** `null` couvre indifféremment « pas de contexte 2D », « décodage
   * refusé » et « mémoire insuffisante », et dans les trois cas la conduite est la même : le
   * dessin reste en JSON dans le relevé, donc rien n'est perdu — seule l'image d'archive
   * manque, et l'écran le dit (M21). Lever ferait tomber l'onglet sur une fiche qui porte la
   * saisie de la matinée.
   */
  render(board: SVGSVGElement, frame: SchematicExportFrame): Promise<Blob | null>
}

/**
 * Les propriétés qui font l'**apparence** d'une forme. Elles viennent du module CSS, donc de
 * jetons du design system : les recopier dans un second moteur de rendu ferait diverger
 * l'image archivée de ce que l'agent a vu.
 *
 * ⚠️ D'où le parti pris : on **clone le plan vivant** et on y fige ce que le navigateur a déjà
 * calculé. Un SVG sérialisé sort de son document — les classes du module et les variables CSS
 * n'y valent plus rien, et l'image sortirait noire sur noir.
 */
const PAINTED = [
  'color',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'font-size',
  'font-family',
  'font-weight',
] as const

/** Assez pour un schéma de branchement ; au-delà le poids monte sans qu'on voie la différence. */
const JPEG_QUALITY = 0.9

export function createSchematicImagePort(): SchematicImagePort {
  return {
    async render(board, frame) {
      const svg = paintedCopy(board, frame)
      const image = await decoded(svg)

      if (image === null) return null

      const canvas = document.createElement('canvas')
      canvas.width = frame.width
      canvas.height = frame.height

      const context = canvas.getContext('2d')
      if (context === null) return null

      // ⚠️ Le fond blanc n'est pas décoratif : le JPEG n'a pas de couche alpha, et un canvas
      // neuf est transparent — sans lui le schéma sortirait blanc sur noir, c'est-à-dire
      // illisible, dans l'archive qu'il existe pour rendre lisible.
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0, canvas.width, canvas.height)

      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', JPEG_QUALITY)
      })
    },
  }
}

/**
 * Une copie du plan, cadrée sur le dessin et **peinte de ce que le navigateur a calculé**.
 *
 * L'appariement se fait par l'ordre du parcours : le clone a exactement le même arbre que la
 * source, donc `querySelectorAll('*')` rend les deux listes dans le même ordre.
 *
 * ⚠️ **Exportée pour être éprouvée**, et c'est l'hypothèse la plus fragile de ce module : le
 * jour où quelqu'un insérerait un nœud dans le clone, l'appariement se décalerait d'un cran
 * **sans rien lever** — chaque forme prendrait la peinture d'une autre, au pire noire sur noir.
 * Ce qui échappe à jsdom est le pixel, pas cette logique-là.
 */
export function paintedCopy(board: SVGSVGElement, frame: SchematicExportFrame): SVGSVGElement {
  const copy = board.cloneNode(true) as SVGSVGElement
  const { view } = frame

  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  copy.setAttribute('viewBox', `${view.x} ${view.y} ${view.width} ${view.height}`)
  copy.setAttribute('width', String(frame.width))
  copy.setAttribute('height', String(frame.height))
  copy.removeAttribute('class')

  const sources = board.querySelectorAll('*')
  const targets = copy.querySelectorAll('*')

  sources.forEach((source, index) => {
    const target = targets[index]
    if (target === undefined) return

    const computed = window.getComputedStyle(source)
    const painted = PAINTED.map((property) => `${property}:${computed.getPropertyValue(property)}`)

    target.setAttribute('style', painted.join(';'))
    target.removeAttribute('class')
  })

  // ⚠️ **Les repères d'édition ne partent pas dans l'archive** — grille, poignées, tracé en
  // cours : ils aident la main, pas le lecteur. Retirés **après** la peinture, qui apparie
  // source et copie par leur rang : les retirer avant décalerait chaque style sur son voisin.
  copy.querySelectorAll('[data-export="skip"]').forEach((element) => element.remove())

  return copy
}

/**
 * Le SVG décodé en image, ou `null` si le navigateur s'y refuse.
 *
 * ⚠️ Une **URL de données**, pas un `URL.createObjectURL` : Safari refuse de décoder un SVG
 * servi par un blob dans un `<img>`, et c'est le navigateur d'une des deux familles de
 * tablettes. `encodeURIComponent` plutôt que `btoa` — le schéma porte des libellés accentués,
 * que `btoa` refuse.
 *
 * ⚠️ `createImage` existe pour que cette promesse-là soit **éprouvée** : jsdom ne charge aucune
 * ressource, donc un `<img>` réel n'y émet jamais `load` ni `error` et la raison écrite
 * ci-dessus ne serait garantie par rien. Même intention que la prop `image` de l'éditeur.
 */
export function decoded(
  svg: SVGSVGElement,
  createImage: () => HTMLImageElement = () => new Image(),
): Promise<HTMLImageElement | null> {
  const markup = new XMLSerializer().serializeToString(svg)
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`

  return new Promise((resolve) => {
    const image = createImage()

    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = source
  })
}
